// frontend/src/components/ScientificDashboard/ScientificDashboard.tsx

import React, { useState } from 'react';
import { useScientificDashboard } from '../../hooks/useScientificDashboard';
import { ScientificSelector } from './ScientificSelector';
import { ReportViewer } from './ReportViewer';
import { useAuth } from '../../contexts/AuthContext';
import './ScientificDashboard.css';

// ============================================================
// Helpers de formatação
// ============================================================
const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const fmt = (value: any, digits = 2, fallback = 'N/A'): string => {
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits);
};

// ============================================================
// Componentes visuais
// ============================================================
const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #1a4a7a',
    marginBottom: '20px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
  }}>
    {children}
  </div>
);

const SectionTitle: React.FC<{ icon: string; question: string }> = ({ icon, question }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
    <span style={{ fontSize: '32px' }}>{icon}</span>
    <h3 style={{ color: '#4fc3f7', margin: 0, fontSize: '20px' }}>❓ {question}</h3>
  </div>
);

const Part: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: '16px' }}>
    <div style={{ fontSize: '12px', color: '#8899aa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
      {title}
    </div>
    {children}
  </div>
);

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '12px', color: '#8899aa' }}>{label}</div>
    <div style={{ fontSize: '14px', color: '#ffffff', marginTop: '2px', wordBreak: 'break-word' }}>
      {renderValue(value)}
    </div>
  </div>
);

const FieldGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>{children}</div>
);

const DataTable: React.FC<{ columns: string[]; rows: any[][] }> = ({ columns, rows }) => (
  <div style={{ overflowX: 'auto', background: 'rgba(10, 10, 26, 0.4)', borderRadius: '8px', padding: '8px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#ffffff' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #1a4a7a' }}>
          {columns.map((c) => (
            <th key={c} style={{ textAlign: 'left', padding: '8px', color: '#8899aa', fontWeight: 500 }}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #16213e' }}>
            {row.map((cell, j) => (
              <td key={j} style={{ padding: '8px' }}>{renderValue(cell)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Explanation: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: 'rgba(79, 195, 247, 0.08)',
    borderLeft: '3px solid #4fc3f7',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '16px',
    color: '#c5cae9',
    fontSize: '14px',
    lineHeight: 1.7,
  }}>
    💡 <strong style={{ color: '#4fc3f7' }}>O que isso significa:</strong> {children}
  </div>
);

const StoryBlock: React.FC<{ text: any }> = ({ text }) => (
  <div style={{
    background: 'rgba(10, 10, 26, 0.4)',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#e0e0e0',
    fontSize: '14px',
    lineHeight: 1.7,
    fontStyle: 'italic',
    borderLeft: '3px solid #4fc3f7',
    whiteSpace: 'pre-wrap',
  }}>
    "{renderValue(text)}"
  </div>
);

// ============================================================
// Componente principal
// ============================================================
export const ScientificDashboard: React.FC = () => {
  const { selectedTraceId, selectedDataset: authDatasetName } = useAuth();

  const {
    traces,
    selectedTrace,
    story,
    loading,
    error,
    selectTrace,
  } = useScientificDashboard(selectedTraceId ?? undefined);

  const [activeTab, setActiveTab] = useState<'overview' | 'business' | 'scientific'>('overview');

  if (loading && !story) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">📖 Carregando história científica...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-state">❌ {error}</div>
      </div>
    );
  }

  if (selectedTraceId && !story) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">⏳ Carregando história do dataset selecionado...</div>
      </div>
    );
  }

  const start = story?.start;
  const know = story?.know;
  const dcp = story?.dcp;
  const edaExplore = story?.eda_explore;
  const edaPrepare = story?.eda_prepare;
  const ml = story?.ml;
  const pipeline: any[] = story?.execution?.pipeline ?? [];

  const datasetName = dcp?.dataset_name || start?.dataset || 'Dataset sem nome';
  const traceId = story?.trace_id;

  const rptBusinessPath = story?.rpt_business?.report_path ?? null;
  const rptScientificPath = story?.rpt_scientific?.report_path ?? null;
  const datasetVersion = dcp?.pipeline_version ?? start?.pipeline_version ?? undefined;

  const columnsMetadata: any[] = Array.isArray(dcp?.columns_metadata) ? dcp.columns_metadata : [];
  const columnRows = columnsMetadata.map((item: any) => {
    const name = Object.keys(item)[0];
    const stats = item[name] || {};
    return [name, stats.dtype, stats.min, stats.max, stats.mean, stats.unique_values, stats.nullable];
  });

  const experiments: any[] = Array.isArray(ml?.experiments) ? ml.experiments : [];

  const pipelineRows = pipeline.map((p: any) => [
    p.nome_amigavel ?? p.stage,
    p.status,
    p.duration_ms,
    p.started_at,
    p.finished_at,
    p.error_message,
  ]);

  const temFailed = pipeline.some((p: any) => p.status === 'FAILED');
  const emAndamento = pipeline.some((p: any) => p.status === 'STARTED' || p.status === 'RUNNING');
  const etapasComErro = pipeline.filter((p: any) => p.error_message);
  const etapasConcluidas = pipeline.filter((p: any) => p.status === 'SUCCESS').length;
  const duracaoTotalMs = pipeline.reduce((s: number, l: any) => s + (l.duration_ms ?? 0), 0);

  const conclusao = temFailed ? 'FALHOU' : emAndamento ? 'EM ANDAMENTO' : 'CONCLUÍDO';
  const conclusaoCor = temFailed ? '#ef5350' : emAndamento ? '#4fc3f7' : '#66bb6a';

  const tabs: { key: 'overview' | 'business' | 'scientific'; label: string; enabled: boolean }[] = [
    { key: 'overview', label: '📊 Visão Geral', enabled: true },
    { key: 'business', label: '📄 Relatório de Negócio', enabled: !!rptBusinessPath },
    { key: 'scientific', label: '📈 Relatório Científico', enabled: !!rptScientificPath },
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>📖 História Científica do Pipeline</h1>
        <p className="subtitle">Perguntas e respostas sobre a jornada dos dados</p>
      </div>

      {/* Abas */}
      {story && (
        <div style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid #1a4a7a',
          marginBottom: '20px',
        }}>
          {tabs.map(({ key, label, enabled }) => (
            <button
              key={key}
              onClick={() => enabled && setActiveTab(key)}
              disabled={!enabled}
              title={!enabled ? 'Relatório não disponível para este dataset/versão' : undefined}
              style={{
                padding: '10px 16px',
                background: 'none',
                cursor: enabled ? 'pointer' : 'not-allowed',
                border: 'none',
                borderBottom: activeTab === key ? '2px solid #4fc3f7' : '2px solid transparent',
                color: !enabled ? '#4a5568' : activeTab === key ? '#4fc3f7' : '#8899aa',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Seletor / dataset em foco */}
      {!selectedTraceId ? (
        <ScientificSelector
          traces={traces}
          selectedTrace={selectedTrace}
          onSelect={selectTrace}
          loading={loading}
        />
      ) : (
        <div style={{
          background: 'rgba(79, 195, 247, 0.1)',
          border: '1px solid #4fc3f7',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          color: '#4fc3f7',
        }}>
          <span>
            Dataset em foco: <strong>{datasetName || authDatasetName || 'Carregando...'}</strong>
          </span>
          <div style={{ fontSize: '11px', marginTop: '4px', color: '#8899aa' }}>
            Para alterar o dataset, acesse a tela "Meus Dados".
          </div>
        </div>
      )}

      {story && (
        <>
          {/* Identificação */}
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
            borderRadius: '12px',
            padding: '16px 20px',
            border: '1px solid #1a4a7a',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#e0e0e0' }}>
              {datasetName}
            </div>
            <div style={{ fontSize: '11px', color: '#8899aa', fontFamily: 'monospace', marginTop: '2px' }}>
              trace_id: {renderValue(traceId)}
            </div>
          </div>

          {/* ABA: Visão Geral */}
          {activeTab === 'overview' && (
            <>
              {/* 1 — START */}
              <Card>
                <SectionTitle icon="🚀" question="O QUE VOCÊ PEDIU?" />
                {start ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: '0 0 12px 0' }}>
                        📋 Você pediu uma análise no dataset <strong>{renderValue(start.dataset)}</strong>,
                        com o target <strong>{renderValue(start.target_name)}</strong>.
                      </p>
                      {start.user_story && (
                        <>
                          <div style={{ fontSize: '12px', color: '#8899aa', marginBottom: '4px' }}>
                            Sua história:
                          </div>
                          <StoryBlock text={start.user_story} />
                        </>
                      )}
                    </Part>
                    <Part title="Detalhes">
                      <FieldGrid>
                        <Field label="Dataset" value={start.dataset} />
                        <Field label="Target" value={start.target_name} />
                        <Field label="Query" value={start.query} />
                        <Field label="Versão" value={start.pipeline_version} />
                        <Field label="Vendor" value={start.vendor} />
                        <Field label="Hash anterior" value={start.previous_dataset_hash} />
                      </FieldGrid>
                    </Part>
                    <Explanation>
                      O <strong>target</strong> é a coluna que você quer prever. A <strong>sua história</strong> é
                      o objetivo do negócio. O <strong>dataset</strong> é a tabela de onde os dados vêm.
                      A <strong>query</strong> é um filtro opcional.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Sem dados de configuração.</p>
                )}
              </Card>

              {/* 2 — DCP */}
              <Card>
                <SectionTitle icon="📊" question="O QUE TEMOS?" />
                {dcp ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                        📊 O dataset <strong>{renderValue(dcp.dataset_name)}</strong> contém{' '}
                        <strong>{renderValue(dcp.total_rows)}</strong> registros e{' '}
                        <strong>{renderValue(dcp.total_columns)}</strong> colunas. Ocupa{' '}
                        <strong>{fmt(dcp.memory_mb, 2)} MB</strong> em memória.
                        {dcp.null_analysis?.total_null_values > 0
                          ? ` ⚠️ ${dcp.null_analysis.total_null_values} valores nulos encontrados.`
                          : ' ✅ Não há valores nulos.'}
                      </p>
                    </Part>
                    <Part title="Detalhes — Colunas">
                      <DataTable
                        columns={['Coluna', 'Tipo', 'Mín', 'Máx', 'Média', 'Únicos', 'Permite nulo']}
                        rows={columnRows}
                      />
                    </Part>
                    <Part title="Detalhes — Análise de Nulos">
                      <DataTable
                        columns={['Campo', 'Valor']}
                        rows={[
                          ['Total de nulos', dcp.null_analysis?.total_null_values],
                          ['Colunas com nulos', dcp.null_analysis?.columns_with_nulls],
                          ['Percentual geral', dcp.null_analysis?.null_percentage_overall],
                        ]}
                      />
                    </Part>
                    <Explanation>
                      O <strong>DCP</strong> lê os dados brutos. Vemos quantas linhas, colunas, o espaço
                      ocupado, e se há valores nulos. Cada linha da tabela é uma coluna do dataset.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Dados do dataset não disponíveis.</p>
                )}
              </Card>

              {/* 3 — EDA EXPLORE */}
              <Card>
                <SectionTitle icon="🎯" question="O QUE QUEREMOS PREVER?" />
                {edaExplore ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                        🎯 Target: <strong>{renderValue(edaExplore.target_name)}</strong>.
                        A classe majoritária representa <strong>{fmt(edaExplore.majority_class_ratio, 3)}</strong> dos dados.
                        A acurácia baseline é <strong>{fmt(edaExplore.baseline_accuracy, 3)}</strong>.
                        A separabilidade máxima é <strong>{fmt(edaExplore.max_separability, 3)}</strong>.
                      </p>
                    </Part>
                    <Part title="Detalhes — Tipos das colunas">
                      {edaExplore.schema ? (
                        <DataTable
                          columns={['Coluna', 'Tipo semântico', 'Tipo físico']}
                          rows={Object.entries(edaExplore.schema).map(([k, v]: [string, any]) => [k, v?.semantic_type, v?.physical_dtype])}
                        />
                      ) : <p style={{ color: '#8899aa' }}>Sem schema registrado.</p>}
                    </Part>
                    <Part title="Detalhes — Distribuição do target">
                      {edaExplore.target_distribution ? (
                        <DataTable
                          columns={['Classe', 'Proporção']}
                          rows={Object.entries(edaExplore.target_distribution).map(([k, v]) => [k, v])}
                        />
                      ) : <p style={{ color: '#8899aa' }}>Sem distribuição registrada.</p>}
                    </Part>
                    <Part title="Detalhes — Separabilidade por feature">
                      {edaExplore.separability ? (
                        <DataTable
                          columns={['Feature', 'Valor']}
                          rows={Object.entries(edaExplore.separability)
                            .filter(([k]) => k !== '_anova')
                            .map(([k, v]) => [k, v])}
                        />
                      ) : <p style={{ color: '#8899aa' }}>Sem separabilidade registrada.</p>}
                    </Part>
                    <Explanation>
                      A <strong>análise exploratória</strong> mostra o desbalanceamento, a separabilidade,
                      e a distribuição do target. A <strong>classe majoritária</strong> indica se há
                      desbalanceamento. A <strong>separação máxima</strong> indica o quão bem as features
                      distinguem o target.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Dados do problema não disponíveis.</p>
                )}
              </Card>

              {/* 4 — EDA PREPARE */}
              <Card>
                <SectionTitle icon="🧹" question="COMO PREPARAMOS OS DADOS?" />
                {edaPrepare ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                        🔧 Selecionamos <strong>{renderValue(edaPrepare.n_features)}</strong> features
                        ({renderValue(edaPrepare.dropped_features_count)} foram removidas).
                        O tipo de problema é <strong>{renderValue(edaPrepare.problem_type)}</strong>.
                        A métrica é <strong>{renderValue(edaPrepare.metric)}</strong>.
                        O split é <strong>{renderValue(edaPrepare.split_strategy)}</strong>.
                      </p>
                    </Part>
                    <Part title="Detalhes — Configuração de treino">
                      {edaPrepare.train_config?.config ? (
                        <FieldGrid>
                          {Object.entries(edaPrepare.train_config.config).map(([k, v]) => (
                            <Field key={k} label={k} value={v} />
                          ))}
                        </FieldGrid>
                      ) : <p style={{ color: '#8899aa' }}>Sem configuração registrada.</p>}
                    </Part>
                    <Part title="Detalhes — Feature schema">
                      {Array.isArray(edaPrepare.feature_schema?.features) ? (
                        <DataTable
                          columns={['Feature', 'Tipo', 'Scaler', 'Missing', 'Outlier']}
                          rows={edaPrepare.feature_schema.features.map((f: any) => [
                            f.name, f.type, f.scaler, f.missing_policy, f.outlier_policy,
                          ])}
                        />
                      ) : <p style={{ color: '#8899aa' }}>Sem feature schema registrado.</p>}
                    </Part>
                    <Part title="Detalhes — Transformações aplicadas">
                      {Array.isArray(edaPrepare.transformations?.decisions) ? (
                        <DataTable
                          columns={['Coluna', 'Decisão', 'Valor', 'Motivo']}
                          rows={edaPrepare.transformations.decisions.map((d: any) => [
                            d.column, d.decision, d.value, d.reason,
                          ])}
                        />
                      ) : <p style={{ color: '#8899aa' }}>Sem transformações registradas.</p>}
                    </Part>
                    <Explanation>
                      A <strong>preparação</strong> define o tipo de problema, a métrica, o split, e as
                      transformações aplicadas. O <strong>train_config</strong> tem os modelos permitidos.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Dados de preparação não disponíveis.</p>
                )}
              </Card>

              {/* 5 — KNOW */}
              <Card>
                <SectionTitle icon="🧠" question="O QUE JÁ SABÍAMOS?" />
                {know?.recommendations && know.recommendations.length > 0 ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                        O sistema encontrou <strong>{know.recommendations.length}</strong> recomendações,
                        com base em datasets similares.
                      </p>
                    </Part>
                    <Part title="Detalhes — Datasets similares">
                      <DataTable
                        columns={['Dataset similar', 'Similaridade', 'Rank']}
                        rows={know.recommendations
                          .filter((r: any, i: number, arr: any[]) =>
                            arr.findIndex((x: any) => x.similar_dataset_name === r.similar_dataset_name) === i
                          )
                          .map((r: any) => [
                            r.similar_dataset_name,
                            r.similarity_score,
                            r.similarity_rank,
                          ])}
                      />
                    </Part>
                    <Part title="Detalhes — Modelos recomendados">
                      <DataTable
                        columns={['Modelo', 'Métrica', 'Score', 'Rank']}
                        rows={know.recommendations
                          .filter((r: any, i: number, arr: any[]) =>
                            arr.findIndex((x: any) => x.model_name === r.model_name) === i
                          )
                          .map((r: any) => [
                            r.model_name,
                            r.experiment_metric,
                            r.experiment_score,
                            r.model_rank,
                          ])}
                      />
                    </Part>
                    <Explanation>
                      O <strong>KNOW</strong> usa o histórico de outros datasets para recomendar modelos
                      que funcionaram bem em problemas parecidos. O <strong>similarity_score</strong> indica
                      o quão parecido o dataset é. O <strong>model_score</strong> indica a performance
                      do modelo recomendado.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Sem recomendações de conhecimento.</p>
                )}
              </Card>

              {/* 6 — ML */}
              <Card>
                <SectionTitle icon="🤖" question="QUAL MODELO FOI MELHOR?" />
                {experiments.length > 0 ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                        🤖 Foram testados <strong>{experiments.length}</strong> modelos.{' '}
                        {ml?.approved?.length > 0
                          ? `${ml.approved.length} foram aprovados.`
                          : 'Nenhum modelo aprovado ainda.'}
                      </p>
                    </Part>
                    <Part title="Detalhes — Comparativo de modelos">
                      <DataTable
                        columns={['Modelo', 'Métrica', 'Valor', 'Decisão', 'Justificativa']}
                        rows={experiments.map((e: any) => [
                          e.model_name, e.metric_name, e.metric_value, e.decision, e.reason,
                        ])}
                      />
                    </Part>
                    <Part title="Detalhes — Relatórios de treino">
                      {experiments.map((e: any, i: number) => (
                        <div key={i} style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', color: '#4fc3f7', marginBottom: '4px' }}>
                            {renderValue(e.model_name)} — {renderValue(e.metric_name)}: {renderValue(e.metric_value)}
                          </div>
                          {e.training_report?.metadata ? (
                            <FieldGrid>
                              {Object.entries(e.training_report.metadata).map(([k, v]) => (
                                <Field key={k} label={k} value={v} />
                              ))}
                            </FieldGrid>
                          ) : <p style={{ color: '#8899aa' }}>Sem relatório.</p>}
                        </div>
                      ))}
                    </Part>
                    <Explanation>
                      O <strong>treinamento</strong> testa vários modelos. A tabela mostra todos, com a
                      <strong> decisão</strong> (APPROVED/REJECTED) e a <strong>justificativa</strong>.
                      O <strong>relatório de treino</strong> tem os hiperparâmetros usados.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Nenhum modelo aprovado ainda.</p>
                )}
              </Card>

              {/* 7 — PIPELINE */}
              <Card>
                <SectionTitle icon="📊" question="O QUE ACONTECEU NA EXECUÇÃO?" />
                {pipeline.length > 0 ? (
                  <>
                    <Part title="Resumo">
                      <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                        ⏱️ Duração total:{' '}
                        <strong>{fmt(duracaoTotalMs / 1000, 1)} segundos</strong>.
                        ✅ {etapasConcluidas} de {pipeline.length} etapas com SUCCESS.
                        {temFailed && ' ❌ FALHOU.'}
                        {!temFailed && emAndamento && ' 🔄 EM ANDAMENTO.'}
                        {!temFailed && !emAndamento && ' ✅ CONCLUÍDO.'}
                      </p>
                      {etapasComErro.length > 0 && (
                        <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(239, 83, 80, 0.1)', borderRadius: '8px' }}>
                          <div style={{ color: '#ef5350', fontSize: '13px' }}>
                            ❌ {etapasComErro[0].error_message}
                          </div>
                        </div>
                      )}
                    </Part>
                    <Part title="Detalhes — Eventos do pipeline">
                      <DataTable
                        columns={['Etapa', 'Status', 'Duração (ms)', 'Início', 'Fim', 'Erro']}
                        rows={pipelineRows}
                      />
                    </Part>
                    <Explanation>
                      Cada linha é um evento do pipeline. O <strong>status</strong> mostra se deu certo
                      (SUCCESS) ou não (FAILED). A <strong>duração</strong> é em milissegundos. O
                      <strong> erro</strong> aparece quando algo falha.
                    </Explanation>
                  </>
                ) : (
                  <p style={{ color: '#8899aa', fontSize: '14px' }}>Dados de execução não disponíveis.</p>
                )}
              </Card>

              {/* 8 — CONCLUSÃO */}
              <Card>
                <SectionTitle icon="✅" question="CONCLUSÃO" />
                <Part title="Resumo">
                  <div style={{
                    padding: '16px 20px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${conclusaoCor}`,
                    color: conclusaoCor,
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '12px',
                  }}>
                    {conclusao === 'FALHOU' && '❌ Execução com falha'}
                    {conclusao === 'EM ANDAMENTO' && '🔄 Execução em andamento'}
                    {conclusao === 'CONCLUÍDO' && '✅ Execução concluída com sucesso'}
                  </div>
                  <p style={{ color: '#e0e0e0', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                    ⏱️ Duração total: <strong>{fmt(duracaoTotalMs / 1000, 1)} segundos</strong>.
                    ✅ {etapasConcluidas} de {pipeline.length} etapas com SUCCESS.
                  </p>
                  {etapasComErro.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#8899aa', marginBottom: '6px' }}>
                        Erros registrados:
                      </div>
                      {etapasComErro.map((e: any, i: number) => (
                        <div
                          key={i}
                          style={{
                            padding: '10px 12px',
                            background: 'rgba(239, 83, 80, 0.1)',
                            borderRadius: '6px',
                            borderLeft: '3px solid #ef5350',
                            color: '#ef5350',
                            fontSize: '13px',
                            marginBottom: '6px',
                          }}
                        >
                          <strong>{renderValue(e.nome_amigavel ?? e.stage)}:</strong> {renderValue(e.error_message)}
                        </div>
                      ))}
                    </div>
                  )}
                </Part>
                <Part title="Detalhes — Etapas">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {pipeline
                      .filter((p: any, i: number, arr: any[]) =>
                        arr.findIndex((x: any) => x.stage === p.stage) === i
                      )
                      .map((p: any, i: number) => {
                        const cor = p.status === 'SUCCESS' ? '#66bb6a'
                          : p.status === 'FAILED' ? '#ef5350'
                          : p.status === 'RUNNING' || p.status === 'STARTED' ? '#4fc3f7'
                          : '#9e9e9e';
                        return (
                          <div
                            key={i}
                            style={{
                              padding: '8px 12px',
                              background: 'rgba(255, 255, 255, 0.03)',
                              borderRadius: '6px',
                              borderLeft: `3px solid ${cor}`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: '12px',
                              fontSize: '14px',
                            }}
                          >
                            <span style={{ color: '#e0e0e0' }}>
                              {renderValue(p.nome_amigavel ?? p.stage)}
                            </span>
                            <span style={{ color: cor, fontWeight: 600 }}>
                              {renderValue(p.status)}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </Part>
                <Explanation>
                  A <strong>conclusão</strong> mostra o status final da execução. Se houve algum erro,
                  ele aparece aqui. Caso contrário, o pipeline foi concluído com sucesso e o modelo
                  está pronto para uso.
                </Explanation>
              </Card>
            </>
          )}

          {/* ABA: Relatório de Negócio */}
          {activeTab === 'business' && (
            <ReportViewer
              title={`Relatório de Negócio — ${datasetName} v${datasetVersion ?? '?'}`}
              containerPath={rptBusinessPath}
              datasetName={datasetName}
              version={datasetVersion}
              kind="business"
            />
          )}

          {/* ABA: Relatório Científico */}
          {activeTab === 'scientific' && (
            <ReportViewer
              title={`Relatório Científico — ${datasetName} v${datasetVersion ?? '?'}`}
              containerPath={rptScientificPath}
              datasetName={datasetName}
              version={datasetVersion}
              kind="scientific"
            />
          )}
        </>
      )}
    </div>
  );
};

export default ScientificDashboard;